// client/src/features/salesCoach/components/PerformanceDashboard.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { salesCoachApi } from "../api/salesCoachApi";
import {
  ArrowLeft,
  BarChart2,
  ShieldAlert,
  Award,
  FileText,
  CheckCircle,
} from "lucide-react";
import Loader from "@/shared/components/ui/Loaders/Loader";

const PerformanceDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  useEffect(() => {
    loadPerformance();
  }, []);

  const loadPerformance = async () => {
    setLoading(true);
    try {
      const res = await salesCoachApi.fetchPerformance();
      setData(res);
    } catch (err) {
      console.error("Failed to load performance metrics:", err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColorClass = (score) => {
    if (score >= 85) return "text-green-600 bg-green-50 border-green-200";
    if (score >= 70)
      return "text-secondary bg-secondary-ultra-light border-[#207C9740]";
    if (score >= 50) return "text-amber-600 bg-amber-50 border-amber-200";
    return "text-rose-600 bg-rose-50 border-rose-200";
  };

  // Compute overall KPI aggregates
  const totalReps = new Set(data.map((d) => d.user_email)).size;
  const overallAvg =
    data.length > 0
      ? Math.round(
          data.reduce((acc, curr) => acc + curr.overall_score, 0) / data.length,
        )
      : 0;

  const objectionsAvg =
    data.length > 0
      ? Math.round(
          data.reduce((acc, curr) => acc + curr.objection_handling_score, 0) /
            data.length,
        )
      : 0;

  const productAvg =
    data.length > 0
      ? Math.round(
          data.reduce((acc, curr) => acc + curr.product_knowledge_score, 0) /
            data.length,
        )
      : 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader text="Loading performance dashboard..." />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-[calc(100vh-60px)] bg-gray-50/50">
      {/* Header */}
      <div className="flex items-center gap-4 pb-6 border-b border-gray-200">
        <Link
          to="/oa/sales-coach"
          className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Award className="w-6 h-6 mr-2 text-secondary" />
            Representative Performance Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Monitor and review detailed sales readiness assessments from rep roleplay sessions.
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-6 md:grid-cols-4 mt-8">
        {[
          {
            label: "Total Sessions Evaluated",
            val: data.length,
            subtitle: `${totalReps} Representatives active`,
          },
          {
            label: "Average Overall Score",
            val: `${overallAvg}%`,
            color: getScoreColorClass(overallAvg),
            subtitle: "Average across all modules",
          },
          {
            label: "Average Objection Handling",
            val: `${objectionsAvg}%`,
            color: getScoreColorClass(objectionsAvg),
            subtitle: "Ability to resolve objections",
          },
          {
            label: "Average Product Knowledge",
            val: `${productAvg}%`,
            color: getScoreColorClass(productAvg),
            subtitle: "Product detail comprehension",
          },
        ].map((kpi, idx) => (
          <div
            key={idx}
            className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex flex-col justify-between"
          >
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              {kpi.label}
            </span>
            <div className="mt-3 flex items-baseline gap-2">
              <span
                className={`text-3xl font-extrabold tracking-tight ${kpi.color || "text-gray-900"}`}
              >
                {kpi.val}
              </span>
            </div>
            <span className="text-xs text-gray-400 mt-2 block">
              {kpi.subtitle}
            </span>
          </div>
        ))}
      </div>

      {/* Performance List table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden mt-8">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">
            Evaluations & Assessments
          </h3>
        </div>

        <div className="overflow-x-auto">
          {data.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm italic">
              No assessments generated yet. Reports appear after representatives complete a roleplay session.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50 text-xs text-gray-400 uppercase font-semibold">
                <tr>
                  <th className="px-6 py-3.5">Representative</th>
                  <th className="px-6 py-3.5">Training Module</th>
                  <th className="px-6 py-3.5 text-center">Overall</th>
                  <th className="px-6 py-3.5 text-center">Product Knowledge</th>
                  <th className="px-6 py-3.5 text-center">
                    Objection Handling
                  </th>
                  <th className="px-6 py-3.5 text-center">Communication</th>
                  <th className="px-6 py-3.5">Completed Date</th>
                  <th className="px-6 py-3.5 text-center">Report</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-medium text-gray-800">
                {data.map((r, idx) => (
                  <tr
                    key={idx}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-bold text-gray-900">
                          {r.user_name}
                        </div>
                        <div className="text-xs text-gray-400">
                          {r.user_email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <span className="font-semibold text-gray-900">
                          {r.product_name}
                        </span>
                        <span className="block text-xs uppercase tracking-wide text-secondary font-bold mt-0.5">
                          {r.mode}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-block px-2.5 py-1 rounded-lg text-xs font-bold border ${getScoreColorClass(r.overall_score)}`}
                      >
                        {r.overall_score}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center font-bold">
                      {r.product_knowledge_score}%
                    </td>
                    <td className="px-6 py-4 text-center font-bold">
                      {r.objection_handling_score}%
                    </td>
                    <td className="px-6 py-4 text-center font-bold">
                      {r.communication_score}%
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-xs">
                      {new Date(r.last_activity).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={async () => {
                          try {
                            const res = await salesCoachApi.fetchAssessment(
                              r.session_id,
                            );
                            setSelectedAssessment({
                              ...res,
                              user_name: r.user_name,
                              product_name: r.product_name,
                            });
                          } catch (err) {
                            console.error(
                              "Failed to load assessment details:",
                              err,
                            );
                          }
                        }}
                        className="p-2 text-secondary bg-secondary-ultra-light hover:bg-[#207C9715] rounded-lg transition-colors inline-flex items-center justify-center"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Drill-down modal */}
      {selectedAssessment && (
        <div className="fixed inset-0 bg-gray-900/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 shadow-2xl relative flex flex-col justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Evaluation Report: {selectedAssessment.user_name}
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                Pitch evaluation for{" "}
                <strong>{selectedAssessment.product_name}</strong>
              </p>

              {/* Score card grid */}
              <div className="grid grid-cols-3 gap-4 mt-6 text-center">
                <div className="p-4 bg-secondary-ultra-light border border-[#207C9730] rounded-2xl col-span-3">
                  <span className="text-xs font-bold text-secondary uppercase tracking-wider">
                    Overall Score
                  </span>
                  <div className="text-4xl font-extrabold text-secondary mt-1">
                    {selectedAssessment.overall_score ??
                      selectedAssessment.overallScore}
                    %
                  </div>
                </div>

                {[
                  {
                    label: "Product Knowledge",
                    val:
                      selectedAssessment.product_knowledge_score ??
                      selectedAssessment.productKnowledgeScore,
                  },
                  {
                    label: "Objection Handling",
                    val:
                      selectedAssessment.objection_handling_score ??
                      selectedAssessment.objectionHandlingScore,
                  },
                  {
                    label: "Communication",
                    val:
                      selectedAssessment.communication_score ??
                      selectedAssessment.communicationScore,
                  },
                  {
                    label: "Feature Understanding",
                    val:
                      selectedAssessment.feature_understanding_score ??
                      selectedAssessment.featureUnderstandingScore,
                  },
                  {
                    label: "Competitive Positioning",
                    val:
                      selectedAssessment.competitive_understanding_score ??
                      selectedAssessment.competitiveUnderstandingScore,
                  },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-gray-50 border border-gray-100 rounded-xl"
                  >
                    <span className="block text-[10px] text-gray-400 uppercase tracking-wider font-bold">
                      {item.label}
                    </span>
                    <span className="text-lg font-bold text-gray-700 mt-1 block">
                      {item.val}%
                    </span>
                  </div>
                ))}
              </div>

              {/* Strengths & Weaknesses lists */}
              <div className="mt-8 space-y-6">
                <div>
                  <h4 className="text-sm font-bold text-green-700 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Key Strengths
                  </h4>
                  <ul className="mt-2 list-disc list-inside text-xs text-gray-600 space-y-1.5 pl-2 font-medium">
                    {(selectedAssessment.strengths || []).map((s, idx) => (
                      <li key={idx}>{s}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-amber-700 flex items-center">
                    <ShieldAlert className="w-4 h-4 mr-2" />
                    Areas of Improvement
                  </h4>
                  <ul className="mt-2 list-disc list-inside text-xs text-gray-600 space-y-1.5 pl-2 font-medium">
                    {(
                      selectedAssessment.improvement_areas ||
                      selectedAssessment.improvementAreas ||
                      []
                    ).map((w, idx) => (
                      <li key={idx}>{w}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-secondary flex items-center">
                    <BarChart2 className="w-4 h-4 mr-2" />
                    Recommendations
                  </h4>
                  <ul className="mt-2 list-disc list-inside text-xs text-gray-600 space-y-1.5 pl-2 font-medium">
                    {(selectedAssessment.recommendations || []).map(
                      (r, idx) => (
                        <li key={idx}>{r}</li>
                      ),
                    )}
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={() => setSelectedAssessment(null)}
                className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-semibold rounded-xl transition-all"
              >
                Close Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceDashboard;
